<template>
	<div class="child-container">
		<SurfDetailHeaderAffix :offset="offsetTop">
			<SurfTabs :tabs="tabs" label-key="label" variant="button" @change="handleTabChange" :use-mob-layout="mobile"/>
		</SurfDetailHeaderAffix>

		<div
			class="surf-content-section item-section"
			v-for="(item, index) in chartAttrs"
			:key="item.value"
			:id="tabs[index].value"
		>
			<div class="chart-header">
				<div class="chart-header-title">{{ tabs[index].label }}</div>
				<div class="chart-header-comparisons">
					<ComparisonSelections
						:activeDate.sync="activeDate[index]"
						:activePeriod.sync="activePeriod[index]"
						@dateChange="initChartData(index)"
						@periodChange="initChartData(index)"
					/>
				</div>
			</div>
			<div class="chart-wrapper">
				<LineBarChart v-bind="item" class="chart-wrapper-box" :isSingleColor="true" :isMobile="mobile" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch } from 'vue';
import SurfTabs from '@rebuild/materials/shared/common/surf/SurfTabs.vue';
import { useModuleConfig } from '@/rebuild/hooks/data/useModuleConfig';
import { useFinanceData } from '../useFinanceData';
import { useFinanceCommon } from '../useFinanceCommon';
import { useSurfScroll } from './useSurfScroll';
import ComparisonSelections from './ComparisonSelections.vue';
import LineBarChart from '@/rebuild/components/charts/bar/lineBar.vue';
import SurfDetailHeaderAffix from '@rebuild/materials/shared/common/surf/SurfDetailHeaderAffix.vue';

const props = defineProps({
	subTabList: {
		type: Array
	},
	mobile: {
		type: Boolean,
		default: false
	}
});

const { offsetTop, calcOffsetTop, handleTabChange: handleTabChangeScroll } = useSurfScroll(props.mobile, (value) => {
	activeTab.value = value;
});

const { config: mainIndexConfig, loadModuleConfig } = useModuleConfig();
const { loadData, getChartDataFunc } = useFinanceData();
const { sliceDataByDate, processTabs, getComparisonPeriod } = useFinanceCommon();

const tabs = ref([]);
const activeTab = ref('');
const activeDate = ref([]);
const activePeriod = ref([]);

const loadSubIndex = async () => {
	let sub;
	if (props.subTabList) {
		sub = props.subTabList;
	} else {
		await loadModuleConfig('main_index');
		sub = mainIndexConfig.value?.tab || [];
	}

	if (sub.length > 0) {
		tabs.value = processTabs(sub);
		activeTab.value = tabs.value[0].value;

		tabs.value.forEach(item => {
			activeDate.value.push(5);
			activePeriod.value.push('A');
		});
		calcOffsetTop();
		await getMainData();
	}
};

const resData = ref([]);
const getChatOriginData = index => {
	return originData.value[activePeriod.value[index]];
};
const chartAttrs = ref([]);

function initChartData(index, isInit = false) {
	if (!props.subTabList?.length) return;
	const dataList = sliceDataByDate(getChatOriginData(index), activeDate.value[index]);
	const fn = getChartDataFunc('profit_statement');
	if (tabs.value?.length) {
		if (!isInit) {
			chartAttrs.value[index] = [];
			chartAttrs.value[index] = fn(tabs.value[index].value, dataList);
		} else {
			chartAttrs.value = [];
			tabs.value.forEach(item => {
				chartAttrs.value.push(fn(item.value, dataList));
			});
		}
		if (props.mobile) {
			chartAttrs.value.forEach(item => {
				item.chartOptions = {
					legend: { show: false },
					yAxis: [
						{
							axisLabel: {
								show: true
							}
						}
					]
				};
				item.legends = item.seriesData.map(item => item.name);
			});
		}
	}
}
const originData = ref({});
async function getMainData() {
	if (!props.subTabList?.length) return;
	await Promise.all(
		getComparisonPeriod.value.map(async item => {
			const res = await loadData('profit_statement', item.value);
			originData.value[item.value] = res.data.sort((a, b) => a.year - b.year);
		})
	);
	tabs.value.forEach(item => {
		resData.value.push(originData.value['A']);
	});
	initChartData(0, true);
}

const handleTabChange = (index, item) => {
	handleTabChangeScroll(index, item);
};

watch(
	() => props.subTabList,
	val => {
		if (!val?.length) {
			resData.value = [];
		} else {
			loadSubIndex();
		}
	},
	{ immediate: true, deep: true }
);
</script>

<style lang="less" scoped>
.item-section {
	& + .item-section {
		margin-top: 20px;
	}
}

.mb20 {
	margin-bottom: 20px;
}
.mt20 {
	margin-top: 20px;
}
.chart-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	.chart-header-title {
		color: var(--surf-inverse-color);
		font-size: 16px;
		font-weight: 500;
		line-height: 24px; /* 150% */
	}
}
.chart-wrapper {
	.chart-wrapper-box {
		width: 100%;
		height: 256px;
	}
}
.chart-header-comparisons {
	flex-shrink: 0;
}
</style>
